import {
  Collapse,
  TableCell,
  TableRow,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { AxiosError, AxiosResponse } from 'axios';
import React, { Fragment, useEffect, useRef, useState, JSX } from 'react';
import { GetSelectedOrderDetailsAPI } from '../../../api/oms/order-details';
import { GetDeliveryTrackerInfo } from '../../../hooks/delivery-tracker-hooks';
import {
  useCommonContext,
  useExperienceContext,
  usePurchaseHistoryContext,
} from '../../../hooks/useContexts';
import { PRINT_LABEL_MINIMUM_VERSION } from '../../../constants/returns';
import { IOrderDetailsResponse } from '../../../models/order-details';
import { IOrder, IOrderParams } from '../../../models/purchase-history';
import {
  ITableRow,
  TSelectedRow,
} from '../../../models/purchase-history/table';
import { getWindow } from '../../../utils/general';
import {
  checkTHDConsumerVersion,
  getMobileOSAndVersion,
} from '../../../utils/shared/mobile';
import { generateOrderDetailsRequest, sliceLongText } from '../util/tableUtils';
import { ExpandedTableRow } from './index';
import { orderNumberParams, retrieveLineItems } from '../util/shared';
import { retrieveSelectedOrderDetail } from '../../../utils/purchase-history/helpers';
import { PHTableData } from './PHTableData';
import { PHTableMenu } from './PHTableMenu';

interface TableData {
  content: string | React.JSX.Element;
  showTableData: boolean;
  align?: string | undefined;
  style?:
    | {
        width: string;
      }
    | undefined;
  verticalAlign?: string | undefined;
}

export const PHTableRow = ({
  order,
  index,
  selected,
  setSelected,
  setAlertState,
  customerType,
  orderHistoryData,
  searchEnabled,
  pHRequest,
  tableHeaderState,
  fetchOrderDetailsAfterSearch,
}: ITableRow): JSX.Element => {
  const { allOrderDetails, setAllOrderDetails, cpsDataSource } =
    usePurchaseHistoryContext();
  const { isConsumerApp, channel } = useExperienceContext();
  const rowRef = useRef<HTMLTableRowElement>(null);
  const [orderDetailsError, setOrderDetailsError] = useState<AxiosError | null>(
    null,
  );
  const [orderDetailsLoaded, setOrderDetailsLoaded] = useState<boolean>(false);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] =
    useState<boolean>(false);
  const { svocID, userID } = useThdCustomer();
  const { timezone } = useCommonContext();

  const selectedOrderDetail = retrieveSelectedOrderDetail(
    order,
    allOrderDetails,
    customerType,
    cpsDataSource,
  );

  useEffect(() => {
    if (selectedOrderDetail) {
      setOrderDetailsLoaded(true);
    }
    if (searchEnabled === false || fetchOrderDetailsAfterSearch) {
      const observer = new IntersectionObserver(
        ([entry]: IntersectionObserverEntry[]) => {
          if (
            entry.isIntersecting &&
            orderDetailsLoaded === false &&
            !selectedOrderDetail &&
            !isLoadingOrderDetails
          ) {
            setIsLoadingOrderDetails(true);
            GetSelectedOrderDetailsAPI(
              generateOrderDetailsRequest(
                order as IOrder,
                customerType,
                svocID,
                timezone,
              ),
              channel,
              userID,
            )
              .then((response: AxiosResponse<IOrderDetailsResponse>) => {
                GetDeliveryTrackerInfo(response.data, userID).then(
                  (deliveryTrackingData) => {
                    setAllOrderDetails((prevDetails) =>
                      deliveryTrackingData
                        ? [
                            ...prevDetails,
                            deliveryTrackingData as IOrderDetailsResponse,
                          ]
                        : prevDetails,
                    );
                  },
                );
              })
              .catch((error: AxiosError) => {
                setOrderDetailsError(error);
              })
              .finally(() => {
                setOrderDetailsLoaded(true);
                setIsLoadingOrderDetails(false);
              });
          }
        },
        { root: null, rootMargin: '0px', threshold: 0.1 },
      );
      if (rowRef.current) {
        observer.observe(rowRef.current);
      }
      return () => {
        if (rowRef.current) {
          observer.unobserve(rowRef.current);
        }
      };
    }
    return () => null;
  }, [selectedOrderDetail, orderDetailsLoaded, isLoadingOrderDetails]);

  const handleRowClick = (id: number) => {
    setSelected((prevSelected: TSelectedRow) => {
      return {
        ...prevSelected,
        [id]: selected[id] === id ? null : id,
      };
    });
  };

  const orderParams: IOrderParams = {
    orderNumbers: orderNumberParams(order as IOrder),
    salesDate: (order as IOrder).salesDate ?? '',
    storeNumber: (order as IOrder).storeNumber ?? 0,
    transactionId: (order as IOrder).transactionId ?? 0,
    transactionIdWithCheckDigit: (order as IOrder).transactionIdWithCheckDigit,
    orderOrigin: (order as IOrder)?.orderOrigin ?? order?.detailsSource ?? '',
    registerNumber: (order as IOrder).registerNumber ?? 0,
    transactionType: (order as IOrder).transactionType ?? '',
    index,
    nickName: (order as IOrder)?.nickName ?? '',
    POJobName: (order as IOrder)?.POJobName ?? undefined,
  };

  const allLineItemImages = retrieveLineItems(selectedOrderDetail);

  const isB2BOnlineOrder =
    customerType === 'b2b' && (order as IOrder)?.orderNumbers?.[0] === 'W';

  const allowNativeAppInStoreReceiptPrint: boolean =
    getWindow()?.__EXPERIENCE_PROPS__.expConfiguration
      .allowNativeAppInStoreReceiptPrint === 'true' || false;

  const appInfo = getMobileOSAndVersion();
  const isSufficientVersion = checkTHDConsumerVersion(
    appInfo.version,
    PRINT_LABEL_MINIMUM_VERSION[appInfo.os],
  );

  const showViewReciptLink =
    (allowNativeAppInStoreReceiptPrint && isSufficientVersion) ||
    !isConsumerApp;

  const formatOrderOrigin = (text: string) => {
    const tempText = text.split(',');
    return (
      <div className="sui-text-base">
        <Typography>{tempText?.[0]}</Typography>
        {tempText?.[1] && (
          <Typography>{sliceLongText(tempText?.[1], 12)}</Typography>
        )}
      </div>
    );
  };

  const searchByObj = pHRequest?.searchBy?.[0] ?? {};

  const tableData = PHTableData(
    allLineItemImages,
    formatOrderOrigin,
    handleRowClick,
    index,
    order,
    orderDetailsLoaded,
    orderParams,
    searchByObj,
    searchEnabled,
    selected,
    setAlertState,
    tableHeaderState,
    selectedOrderDetail,
    orderDetailsError,
  );

  const emptyHeaders: TableData[] = [];

  return (
    <Fragment key={index}>
      <TableRow
        ref={rowRef}
        lastVisible={index === orderHistoryData.length}
        style={{ height: '68px' }}
      >
        {tableData?.map((item, indexJ) => {
          if (item?.showTableData === false) {
            emptyHeaders.push(item);
            return null;
          }
          if (item?.align === 'center') {
            return (
              <TableCell
                key={indexJ}
                style={item?.style}
                valign={item?.verticalAlign}
                align={item?.align}
              >
                {item?.content}
              </TableCell>
            );
          }
          return (
            <TableCell
              key={indexJ}
              variant="body"
              padding="none"
              style={item?.style}
            >
              {item?.content}
            </TableCell>
          );
        })}
        {emptyHeaders?.map((item, indexK) => {
          if (indexK < 3) {
            return null;
          }
          return (
            <TableCell
              key={indexK}
              variant="body"
              padding="none"
              style={item?.style}
            >
              <div />
            </TableCell>
          );
        })}
        <TableCell variant="body" padding="none" style={{ maxWidth: '36px' }}>
          <PHTableMenu
            accountIdValue={(order as IOrder)?.accountIdValue}
            accountIdType={(order as IOrder)?.accountIdType}
            customerType={customerType}
            enableReceipt={
              !(selectedOrderDetail as IOrderDetailsResponse)?.isAAFESOrder &&
              !(selectedOrderDetail as IOrderDetailsResponse)?.isNEXOrder &&
              !isB2BOnlineOrder &&
              ((selectedOrderDetail as IOrderDetailsResponse)
                ?.showReceiptImage ||
                Boolean(selectedOrderDetail?.receiptImageSting)) &&
              showViewReciptLink
            }
            encryptedReceiptRequest={
              (order as IOrder)?.encryptedReceiptRequest ?? ''
            }
            orderDetailsData={selectedOrderDetail as IOrderDetailsResponse}
            orderNumber={
              (order as IOrder)?.orderNumber ??
              (order as IOrder)?.orderNumbers?.[0] ??
              ''
            }
            orderOrigin={(order as IOrder).orderOrigin ?? ''}
            orderParams={orderParams}
            receiptImageSting={
              (selectedOrderDetail as IOrderDetailsResponse)
                ?.receiptImageSting ?? ''
            }
            setAlertState={setAlertState}
          />
        </TableCell>
      </TableRow>
      <TableRow collapsed={selected[index] !== index}>
        <TableCell
          colSpan={12}
          style={{
            padding: !searchEnabled ? '10px 40px 10px 45px' : '0px',
          }}
          padding="none"
        >
          <Collapse in={selected[index] === index}>
            {!searchEnabled && (
              <ExpandedTableRow
                orderDetailsData={selectedOrderDetail}
                orderDetailsError={orderDetailsError}
                orderDetailsLoaded={orderDetailsLoaded}
              />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};
